import {
  ApiArrearsResponse,
  toArrears
} from 'Feature/Arrears/components/ArrearsInquiryPage/api/ApiArrearsResponse';
import { toApiArrearsUpdateCommand } from 'Feature/Arrears/components/ArrearsInquiryPage/api/ApiArrearsUpdateCommand';
import {
  ApiPagedSuccessResponse,
  ApiSuccessResponse,
  toPaging
} from 'Feature/Arrears/components/ArrearsInquiryPage/api/ApiResponse';
import { Arrears, PagedArrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Arrears';
import { Language } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Language';
import { ValidLoanNumber } from 'Feature/Arrears/components/ArrearsInquiryPage/core/LoanNumber';
import { Ordering } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Ordering';
import { Paging } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Paging';
import { Result } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Result';
import { ApiClient } from 'Foundation/Api';
import { AuthenticationContextType } from 'Foundation/Authentication';

export interface ArrearsApi {
  get: (
    loanNumber: ValidLoanNumber,
    paging: Paging,
    ordering: Ordering
  ) => Promise<Result<PagedArrears, string>>;
  update: (arrears: Arrears) => Promise<Result<Arrears, string[]>>;
  del: (id: string) => Promise<Result<boolean, string[]>>;
}

export const arrearsApi = (
  auth: AuthenticationContextType,
  baseUrl: string,
  language: Language
): ArrearsApi => {
  const { getWithAuth } = ApiClient(auth, { timeout: 10000 });

  const get = async (
    loanNumber: ValidLoanNumber,
    paging: Paging,
    ordering: Ordering
  ): Promise<Result<PagedArrears, string>> => {
    try {
      const url = encodeURI(
        `${baseUrl}/search?skip=${paging.skip}&take=${paging.take}&filterBy=loanNumber -EQ ${loanNumber.value}&orderBy=${ordering.field} ${ordering.direction}`
      );
      const response = await getWithAuth(url);
      const apiResponse = response.data as ApiPagedSuccessResponse<ApiArrearsResponse>;
      return Result.success({
        data: apiResponse.data.map((a) => toArrears(a, language)),
        paging: toPaging(apiResponse.page)
      });
    } catch (error) {
      const apiError = error as any;
      return Result.failure(apiError.response?.data?.error?.errorCode ?? 'unknown-error');
    }
  };

  const update = async (arrears: Arrears): Promise<Result<Arrears, string[]>> => {
    try {
      const { putWithAuth } = ApiClient(auth, { timeout: 10000 });
      const url = encodeURI(`${baseUrl}/update/${arrears.id}`);
      const response = await putWithAuth(url, toApiArrearsUpdateCommand(arrears));
      const apiResponse = response.data as ApiSuccessResponse<ApiArrearsResponse>;

      return Result.success(toArrears(apiResponse.data, language));
    } catch (error) {
      const apiError = error as any;
      const errors = apiError.response?.data?.errors?.map((e: any) => e.code);
      return Result.failure(errors ?? ['unknown-error']);
    }
  };

  const del = async (id: string): Promise<Result<boolean, string[]>> => {
    try {
      const { deleteWithAuth } = ApiClient(auth, { timeout: 10000 });
      const url = encodeURI(`${baseUrl}/delete/${id}`);
      await deleteWithAuth(url);

      return Result.success(true);
    } catch (error) {
      const apiError = error as any;
      const errors = apiError.response?.data?.errors?.map((e: any) => e.code);
      return Result.failure(errors ?? ['unknown-error']);
    }
  };

  return { get, update, del };
};
